// typescript
import React from "react";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import { ThoughtButtons } from "@/main/react/thoughts/ThoughtButtons";
import { Localizer } from "@/main/react/Types";
import { ThoughtInConversationDTO } from "@/generated-client";
import DateFormatter from "@/main/common/DateFormatter";
import {getDisplayUsername, getUserProfilePath, hasDisplayUsername} from "@/main/common/UsernameDisplayUtils";
import {scrollElementIntoViewWithOffset} from "@/main/common/ScrollUtils";
import {
    ArgumentDeletedTombstone,
    ThoughtLifecycleFields,
    canDeleteArgument,
    isArgumentDeleted
} from "@/main/react/thoughts/ThoughtLifecycle";
import SensitiveMediaGuard from "@/main/react/sensitive/SensitiveMediaGuard";
import { getMaskedContentWarningCopy } from "@/main/react/sensitive/ContentWarningCopy";
import {cn} from "@/lib/utils";
import { useViewerActionRecord } from "@/main/common/ViewerActionRecords";
import type { ThoughtEditFields } from "@/main/common/ThoughtEditTypes";
import TranslateControlLazy from "@/main/translation/TranslateControlLazy";
import type { TranslationResult } from "@/main/translation/TranslateControl";
import { hasTranslatorBackend } from "@/main/translation/translatorDetection";

export default function ThoughtDisplayerInConversation(props: {
    argument: ThoughtInConversationDTO;
    conversationPublicId: string;
    loggedInUsername?: string;
    isUserLoggedIn: boolean;
    agreementAgreementTitle: string;
    disagreementAgreementTitle: string;
    localizer: Localizer;
    displayOrientation?: boolean;
    isSensitive?: boolean;
    suppressSpoilerGuard?: boolean;
    isHighlighted?: boolean;
}) {
    const {
        argument,
        conversationPublicId,
        localizer,
        displayOrientation = true,
        isUserLoggedIn,
        loggedInUsername,
        isSensitive = false,
        suppressSpoilerGuard = false,
        isHighlighted = false,
    } = props;

    const [displayArgument, setDisplayArgument] = React.useState(argument);

    React.useEffect(() => {
        setDisplayArgument(argument);
    }, [argument]);

    // Editable replies (design §3.6): this surface offers no edit of its own, but it must still show the
    // viewer's own edit and the "Edited" marker — `record ?? prop`, exactly as ThoughtDisplayerInDiscussion.
    const [contentRecord] = useViewerActionRecord("thought-content", displayArgument.publicId ?? null, null);
    const effectiveHtml = contentRecord ? contentRecord.htmlContent : displayArgument.htmlContent;
    const effectiveEditedAt = contentRecord
        ? contentRecord.editedAt
        : ((displayArgument as ThoughtInConversationDTO & ThoughtEditFields).editedAt ?? null);

    const rootRef = React.useRef<HTMLDivElement | null>(null);

    const dateFormatter = new DateFormatter(localizer);
    /*
     * QA-18: the relative stamp's absolute twin, for its <time title>. Memoized per row because this is
     * the hot reply path and `formatDateTime` builds an `Intl.DateTimeFormat` on every call.
     */
    const createdAtIso = displayArgument.createdAt ?? undefined;
    const createdAtAbsolute = React.useMemo(
        () => (createdAtIso ? new DateFormatter(localizer).formatDateTime(createdAtIso) : undefined),
        [createdAtIso, localizer],
    );
    const lifecycleArgument = displayArgument as ThoughtInConversationDTO & ThoughtLifecycleFields;
    const spoilerableArgument = displayArgument as ThoughtInConversationDTO & { spoiler?: boolean | null };
    const deleted = isArgumentDeleted(lifecycleArgument);

    // SV-140 wave 2 (A4-A6): the reply's own stored language drives `lang` and the translate control.
    // A translation is STATE keyed on the HTML it was made from, rendered through the same body
    // container; an edit changes `effectiveHtml`, so a stale translation can never paint over it (rule 32).
    const replyLanguageTag = displayArgument.languageTag || null;
    const replyBodyHtml = effectiveHtml ?? "";
    const [translation, setTranslation] = React.useState<(TranslationResult & { forHtml: string }) | null>(null);
    const activeTranslation = translation && translation.forHtml === replyBodyHtml ? translation : null;
    const displayedBodyHtml = activeTranslation ? activeTranslation.html : replyBodyHtml;
    const displayedLanguageTag = activeTranslation ? activeTranslation.lang : (replyLanguageTag ?? undefined);
    const onTranslationDisplayChange = React.useCallback((next: TranslationResult | null) => {
        setTranslation(next ? { ...next, forHtml: replyBodyHtml } : null);
    }, [replyBodyHtml]);
    // Feature detection once per mount: no translator means no control and no chunk fetch (rule 27).
    const [translatorPresent] = React.useState(hasTranslatorBackend);
    const offerTranslation = translatorPresent && !deleted && Boolean(replyLanguageTag) && Boolean(replyBodyHtml);
    const isSpoiler = !suppressSpoilerGuard && Boolean(contentRecord ? contentRecord.spoiler : spoilerableArgument.spoiler);
    const warningCopy = React.useMemo(
        () => getMaskedContentWarningCopy(localizer, { isSensitive, isSpoiler }),
        [isSensitive, isSpoiler, localizer],
    );
    const hasAuthorUsername = hasDisplayUsername(displayArgument.username);
    const displayAuthorUsername = getDisplayUsername(displayArgument.username, localizer);
    const authorProfilePath = getUserProfilePath(displayArgument.username);
    const bookmarkableArgument = displayArgument as ThoughtInConversationDTO & ThoughtLifecycleFields & {
        argumentBookmarksCount?: number;
        argumentFollowersCount?: number;
        isBookmarked?: boolean;
        isFollowed?: boolean;
    };

    const isAuthorTheLoggedInUser =
        isUserLoggedIn && loggedInUsername === displayArgument.username;

    // In a conversation the buttons follow their chat bubble: the opening side keeps
    // standard order hugging its (left) bubble; the replying side is reversed and
    // hugs its (right) bubble.
    const buttonWrapperClass = cn(
        "react-component mt-2 flex justify-end gap-2",
        displayOrientation ? "mr-auto sm:flex-row" : "ml-auto sm:flex-row-reverse",
    );
    const headerWrapperClass = cn("w-full flex", displayOrientation ? "justify-start" : "justify-end");
    const headerAnchorClass = cn("flex items-center gap-3 no-underline", displayOrientation ? undefined : "sm:flex-row-reverse");
    const contentColumnClass = cn("mt-3 flex w-full flex-col", displayOrientation ? "items-start pl-6" : "items-end pr-6");
    const surfacedBodyWrapperClass = "w-full max-w-[72ch]";
    const surfacedBodyClass = cn(
        "w-full min-w-0 text-sm! leading-7",
        isSensitive || isSpoiler ? "min-h-[5rem]" : undefined,
    );
    // Chat bubble around the message body — same subtle surface as the discussion-view cards, with
    // the near corner mirrored toward the speaker's side (symmetric; neither side emphasized).
    const bubbleClass = cn(
        "w-full max-w-[72ch] rounded-[1.5rem] border border-border/60 bg-[color-mix(in_srgb,var(--card)_90%,var(--background))] px-4 py-3",
        displayOrientation ? "rounded-tl-md" : "rounded-tr-md",
    );
    const deletedSurfaceClass = "w-full max-w-[72ch] rounded-[1.5rem] border border-border/60 bg-card/92 px-4 py-4";
    const rootClassName = cn(
        "relative flex w-full max-w-full flex-col rounded-[1.75rem] p-2 transition-colors lg:mx-auto lg:max-w-[78ch]",
        isHighlighted ? "bg-primary/5 ring-1 ring-primary/35" : undefined,
    );

    React.useEffect(() => {
        if (!isHighlighted || !rootRef.current) {
            return;
        }

        scrollElementIntoViewWithOffset(rootRef.current, {attempts: 1, align: "middle"}).catch(() => {
            // ignore scrolling failures for targeted permalinks
        });
    }, [isHighlighted]);

    return (
        <div
            ref={rootRef}
            id={displayArgument.publicId ? `argument-${displayArgument.publicId}` : undefined}
            data-thought-public-id={displayArgument.publicId ?? ""}
            // The stable hook for the highlight, added 2026-08-09 because the permalink had NO
            // assertion-bearing coverage: every spec asserted the redirect and the URL, so the one
            // thing the permalink exists to do could break silently and stay green. Written on every
            // card, not only the highlighted one, so a spec can assert the INVARIANT — this card is
            // highlighted and its siblings are not — rather than a class string that any restyle breaks.
            data-highlighted={isHighlighted ? "true" : "false"}
            className={rootClassName}
        >
            <div className={headerWrapperClass}>
                {hasAuthorUsername && authorProfilePath ? (
                    <a href={authorProfilePath} className={headerAnchorClass}>
                        <ProfileImage localizer={localizer}
                            imageUrl={displayArgument.profileImageUrl}
                            username={displayArgument.username}
                            interactive={true}
                            mode={"user"}
                            hoverable
                            className={"size-10 sm:size-12"}
                        />
                        <div className="flex flex-col">
                            <div className="text-sm font-semibold text-foreground"
                                 translate={hasDisplayUsername(displayArgument.username) ? "no" : undefined}>{displayAuthorUsername}</div>

                            <span className="small-meta text-xs text-muted-foreground">
                                <time dateTime={createdAtIso} title={createdAtAbsolute}>
                                    {dateFormatter.timeSince(displayArgument.createdAt!)}
                                </time>
                                {effectiveEditedAt ? (
                                    <>
                                        <span aria-hidden="true"> · </span>
                                        <time
                                            dateTime={effectiveEditedAt}
                                            title={dateFormatter.formatDateTime(effectiveEditedAt)}
                                            data-playwright-id="thought-edited-marker"
                                        >
                                            {localizer("thought.edited")}
                                        </time>
                                    </>
                                ) : null}
                            </span>
                        </div>
                    </a>
                ) : (
                    <div className={headerAnchorClass}>
                        <ProfileImage localizer={localizer}
                            imageUrl={displayArgument.profileImageUrl}
                            username={undefined}
                            interactive={true}
                            mode={"user"}
                            hoverable
                            className={"size-10 sm:size-12"}
                        />
                        <div className="flex flex-col">
                            <div className="text-sm font-semibold text-foreground"
                                 translate={hasDisplayUsername(displayArgument.username) ? "no" : undefined}>{displayAuthorUsername}</div>

                            <span className="small-meta text-xs text-muted-foreground">
                                <time dateTime={createdAtIso} title={createdAtAbsolute}>
                                    {dateFormatter.timeSince(displayArgument.createdAt!)}
                                </time>
                                {effectiveEditedAt ? (
                                    <>
                                        <span aria-hidden="true"> · </span>
                                        <time
                                            dateTime={effectiveEditedAt}
                                            title={dateFormatter.formatDateTime(effectiveEditedAt)}
                                            data-playwright-id="thought-edited-marker"
                                        >
                                            {localizer("thought.edited")}
                                        </time>
                                    </>
                                ) : null}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            <div className={contentColumnClass}>
                {deleted ? (
                    <div className={deletedSurfaceClass}>
                        <ArgumentDeletedTombstone
                            localizer={localizer}
                            deletedAt={lifecycleArgument.deletedAt}
                            deletedByModerator={lifecycleArgument.deletedByModerator}
                            deletedReasonType={lifecycleArgument.deletedReasonType}
                        />
                    </div>
                ) : (
                    <div className={bubbleClass}>
                        <SensitiveMediaGuard
                            isSensitive={isSensitive}
                            isSpoiler={isSpoiler}
                            hideSensitiveMedia={true}
                            variant="inline"
                            className={surfacedBodyWrapperClass}
                            badgeLabel={warningCopy.badgeLabel}
                            label={warningCopy.label}
                            helperText={warningCopy.helperText}
                            revealText={warningCopy.revealText}
                        >
                            <div
                                className={cn("tiptap argument-rich-content", surfacedBodyClass)}
                                data-thought-content-source={contentRecord ? "record" : "dto"}
                                lang={displayedLanguageTag}
                                dangerouslySetInnerHTML={{ __html: displayedBodyHtml }}
                            />
                        </SensitiveMediaGuard>
                    </div>
                )}
                {/* SV-140: purely additive, below the reply body; renders nothing unless the device can
                    translate this reply into another language (A1). */}
                {offerTranslation ? (
                    <TranslateControlLazy
                        html={replyBodyHtml}
                        sourceLang={replyLanguageTag}
                        localizer={localizer}
                        onDisplayChange={onTranslationDisplayChange}
                        playwrightIdPrefix="thought-translate"
                        entity={displayArgument.publicId ? { type: "THOUGHT", publicId: displayArgument.publicId } : null}
                    />
                ) : null}
                <div className={buttonWrapperClass}>
                    <ThoughtButtons
                        argumentPublicId={displayArgument.publicId!}
                        initialAgreementsCount={displayArgument.agreementsCount}
                        initialDisagreementsCount={displayArgument.disagreementsCount}
                        initialBookmarksCount={bookmarkableArgument.argumentBookmarksCount ?? bookmarkableArgument.argumentFollowersCount}
                        initiallyAgreed={displayArgument.isAgreed}
                        initiallyDisagreed={displayArgument.isDisagreed}
                        initiallyBookmarked={bookmarkableArgument.isBookmarked ?? bookmarkableArgument.isFollowed}
                        isOpeningArgument={false}
                        isAuthorTheLoggedInUser={isAuthorTheLoggedInUser}
                        authorUsername={displayArgument.username}
                        conversationPublicId={conversationPublicId}
                        displayConversationButton={false}
                        discussionSlug={""}
                        localizer={localizer}
                        isDeleted={deleted}
                        deletedAt={lifecycleArgument.deletedAt}
                        canDelete={canDeleteArgument(lifecycleArgument)}
                        canStartConversation={false}
                        onArgumentUpdated={(updatedArgument) => setDisplayArgument((current) => ({...current, ...updatedArgument}))}
                    />
                </div>
            </div>
        </div>
    );
}
